import { useCallback, useEffect, useRef, useState } from "react";

export type GeoStatus = "idle" | "prompting" | "granted" | "denied" | "unavailable" | "error";

export interface GeoCoords {
  lat: number;
  lng: number;
  accuracy: number;
}

export interface GeolocationState {
  status: GeoStatus;
  coords: GeoCoords | null;
  error: string | null;
  request: () => void;
  clear: () => void;
}

/**
 * Geolocation hook with explicit request/clear semantics.
 * - No auto-request on mount (privacy-friendly).
 * - When `request()` is called, asks for permission and watches position.
 * - `clear()` stops the watch and forgets the position.
 */
export function useGeolocation(): GeolocationState {
  const [status, setStatus] = useState<GeoStatus>("idle");
  const [coords, setCoords] = useState<GeoCoords | null>(null);
  const [error, setError] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setCoords(null);
    setError(null);
    setStatus("idle");
  }, []);

  const request = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setStatus("unavailable");
      setError("Geolocation API not available");
      return;
    }
    setStatus("prompting");
    setError(null);
    if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setStatus("granted");
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setStatus("denied");
        else setStatus("error");
        setError(err.message);
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 12000 }
    );
  }, []);

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  return { status, coords, error, request, clear };
}
